import React from 'react';
import {
  Box,
  Card,
  Container,
  Divider,
  Grid2 as Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import Label from 'components/Label';

// Reference data
const REFUND_PREFERENCE_OPTIONS = [
  { value: 'pending', label: 'Pending', description: 'Default state when a consumption request is received' },
  { value: 'approved', label: 'Approved', description: 'Approve the refund for eligible requests' },
  { value: 'rejected', label: 'Rejected', description: 'Reject the refund request (default for auto-response)' },
  { value: 'no-preference', label: 'No Preference', description: 'No preference has been indicated' },
  { value: 'undeclared', label: 'Undeclared', description: 'Status has not been set' },
];

const REFUND_PREFERENCE_COLORS = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  'no-preference': 'info',
  undeclared: 'default',
};

export default function ConsumptionRequestsGuide() {
  const theme = useTheme();

  return (
    <Container maxWidth='lg' sx={{ py: 5 }}>
      <Typography variant='h3' component='h1' gutterBottom>
        Consumption Requests Management Guide (Mobeat)
      </Typography>
      <Paper sx={{ p: 3, mb: 4, bgcolor: alpha(theme.palette.info.light, 0.1) }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <InfoIcon color='info' sx={{ mr: 2, mt: 0.5 }} />
          <Typography>
            This guide explains how to process consumption requests and handle refund approvals on the Mobeat platform.
            In most cases, the default action is to reject consumption requests automatically, but when a user submits a
            support ticket requesting a refund, we may need to approve it if it meets certain criteria.
          </Typography>
        </Box>
      </Paper>
      <Grid container spacing={4}>
        {/* Process Overview Section */}
        <Grid size={12}>
          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant='h5' gutterBottom>
              Process Overview
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  1
                </Box>
                <Box>
                  <Typography variant='subtitle1'>User submits a refund request</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    A user contacts customer support requesting a refund for a purchase.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, my: -1 }}>
                <ArrowForwardIcon color='disabled' />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  2
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Verify eligibility</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Check if the purchase was made within the last 7 days and if the user meets other criteria for a
                    refund (low usage, legitimate reason, etc.).
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, my: -1 }}>
                <ArrowForwardIcon color='disabled' />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  3
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Find consumption request</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Search for the consumption request in the Mobeat system using the user ID or transaction ID.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, my: -1 }}>
                <ArrowForwardIcon color='disabled' />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  4
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Update refund status</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Change the refund preference to "Approved" for eligible requests or "Rejected" for ineligible ones.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, my: -1 }}>
                <ArrowForwardIcon color='disabled' />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  5
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Notify the user</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Inform the user about the decision and next steps.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Eligibility Criteria Section */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant='h5' gutterBottom>
              Eligibility Criteria
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Criteria</TableCell>
                    <TableCell>Requirement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Time since purchase</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.16),
                            color: theme.palette.success.main,
                          }}
                        >
                          ≤ 7 days
                        </Label>
                        Eligible
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.error.main, 0.16),
                            color: theme.palette.error.main,
                          }}
                        >
                          &gt; 7 days
                        </Label>
                        Not eligible
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Consumption status</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.16),
                            color: theme.palette.success.main,
                          }}
                        >
                          Not consumed
                        </Label>
                        Eligible
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.warning.main, 0.16),
                            color: theme.palette.warning.main,
                          }}
                        >
                          Partially consumed
                        </Label>
                        Case-by-case
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.error.main, 0.16),
                            color: theme.palette.error.main,
                          }}
                        >
                          Fully consumed
                        </Label>
                        Rarely eligible
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Refund history</TableCell>
                    <TableCell>Consider user's previous refund requests</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Request recency</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.16),
                            color: theme.palette.success.main,
                          }}
                        >
                          ≤ 12 hours
                        </Label>
                        Process normally
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Label
                          sx={{
                            mr: 1,
                            bgcolor: alpha(theme.palette.warning.main, 0.16),
                            color: theme.palette.warning.main,
                          }}
                        >
                          &gt; 12 hours
                        </Label>
                        Ask user to resubmit
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Status Options Section */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant='h5' gutterBottom>
              Refund Preference Options
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>When to Use</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {REFUND_PREFERENCE_OPTIONS.map((option) => (
                    <TableRow key={option.value}>
                      <TableCell>
                        <Label variant='ghost' color={REFUND_PREFERENCE_COLORS[option.value]}>
                          {option.label}
                        </Label>
                      </TableCell>
                      <TableCell>{option.description}</TableCell>
                      <TableCell>
                        {option.value === 'approved' && (
                          <Typography variant='body2'>For eligible requests within 7 days</Typography>
                        )}
                        {option.value === 'rejected' && (
                          <Typography variant='body2'>For ineligible requests or suspicious activity</Typography>
                        )}
                        {option.value === 'pending' && (
                          <Typography variant='body2'>Initial state, needs review</Typography>
                        )}
                        {option.value === 'no-preference' && (
                          <Typography variant='body2'>No decision needed at this time</Typography>
                        )}
                        {option.value === 'undeclared' && <Typography variant='body2'>Not yet processed</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Step-by-Step Guide Section */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant='h5' gutterBottom>
              Step-by-Step Process
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                1. Find the Consumption Request
              </Typography>
              <Box sx={{ pl: 2, borderLeft: `4px solid ${theme.palette.primary.main}`, mb: 2 }}>
                <Typography variant='body1' paragraph>
                  Navigate to the Consumption Requests list page in the Mobeat admin panel:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Applications → [App ID] → Consumption Requests</Typography>
                </Box>
                <Typography variant='body1' paragraph>
                  Use the search filters to locate the request:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Filter by Transaction ID or User ID provided in the support ticket</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>
                    Click the <VisibilityIcon fontSize='small' sx={{ verticalAlign: 'middle', mx: 0.5 }} />
                    icon to view the consumption request details
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                2. Verify Eligibility
              </Typography>
              <Box sx={{ pl: 2, borderLeft: `4px solid ${theme.palette.primary.main}`, mb: 2 }}>
                <Typography variant='body1' paragraph>
                  Check the transaction date to confirm if it's within 7 days:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Look at the "Created At" date in the consumption request details</Typography>
                </Box>
                <Typography variant='body1' paragraph>
                  Review consumption status:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Check if the purchase is unused or partially used</Typography>
                </Box>
                <Typography variant='body1' paragraph>
                  Review user's refund history if available:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Frequent refund requests might indicate abuse</Typography>
                </Box>
                <Typography variant='body1' paragraph>
                  Check the time since the consumption request was created:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>
                    If the consumption request is older than 12 hours, ask the user to request a refund again through
                    the app
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                3. Update the Refund Preference
              </Typography>
              <Box sx={{ pl: 2, borderLeft: `4px solid ${theme.palette.primary.main}`, mb: 2 }}>
                <Typography variant='body1' paragraph>
                  In the consumption request detail view:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Use the dropdown to select the appropriate refund preference</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Choose "Approved" for eligible requests within 7 days</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Choose "Rejected" for ineligible requests</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Click the "Update" button to save your changes</Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant='h6' gutterBottom>
                4. Document and Communicate
              </Typography>
              <Box sx={{ pl: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                <Typography variant='body1' paragraph>
                  Document the decision:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Make a note of the decision and reasoning in your support ticket system</Typography>
                </Box>
                <Typography variant='body1' paragraph>
                  Communicate with the user:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>Inform the user of the decision through the appropriate support channel</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                  <Typography>
                    For stale requests (older than 12 hours): Instruct the user to submit a new refund request through
                    the app as the previous request has expired
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Handling Expired Requests Section */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant='h5' gutterBottom>
              Handling Expired Requests
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography variant='body1' paragraph>
              When a consumption request is older than 12 hours:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pl: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.warning.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  1
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Explain the situation to the user</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Inform them that refund requests must be processed within 12 hours due to platform requirements.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.warning.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  2
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Provide clear instructions</Typography>
                  <Typography variant='body2' color='text.secondary' paragraph>
                    Guide the user to submit a new refund request through the app:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                      <Typography variant='body2'>Open the app</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                      <Typography variant='body2'>Navigate to purchase history</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                      <Typography variant='body2'>Select the transaction</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <NavigateNextIcon color='disabled' sx={{ mr: 1 }} />
                      <Typography variant='body2'>Choose "Request Refund" option</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.warning.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  3
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Monitor for new request</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    After the user submits a new request, it should appear in the system within minutes.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: theme.palette.warning.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  4
                </Box>
                <Box>
                  <Typography variant='subtitle1'>Process promptly</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Process the new request as soon as it appears to avoid another timeout.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Important Notes Section */}
        <Grid size={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant='h5' gutterBottom>
              Important Notes
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <WarningIcon color='warning' sx={{ mr: 2, mt: 0.5 }} />
                <Typography>
                  <strong>Time Sensitivity:</strong> Responses to consumption requests should be prompt, ideally within
                  12 hours of receiving the request. After 12 hours, many platforms consider the request expired.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon color='info' sx={{ mr: 2, mt: 0.5 }} />
                <Typography>
                  <strong>Platform Differences:</strong> App Store and Google Play Store may have different refund
                  policies. Mobeat's system handles the appropriate responses based on the platform.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <CheckCircleIcon color='success' sx={{ mr: 2, mt: 0.5 }} />
                <Typography>
                  <strong>Default Automation:</strong> Most consumption requests are automatically rejected unless
                  explicitly approved. This guide focuses on the manual approval process when needed.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LockIcon color='primary' sx={{ mr: 2, mt: 0.5 }} />
                <Typography>
                  <strong>Mobeat Support Process:</strong> This entire workflow is managed through the Mobeat admin
                  dashboard and should be handled by authorized Mobeat support personnel only.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <HelpIcon color='primary' sx={{ mr: 2, mt: 0.5 }} />
                <Typography>
                  <strong>Questions?</strong> For any questions about handling specific cases, please contact the Mobeat
                  revenue management team.
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
